import React, { useEffect, useState } from 'react';
import { getExecutionCapacity, ExecutionCapacity } from '../../api/executions';

interface CapacityBarProps {
  compact?: boolean; // true = version mini pour le dashboard widget
  refreshInterval?: number; // ms, défaut 15s
}

const CapacityBar: React.FC<CapacityBarProps> = ({ compact = false, refreshInterval = 5000 }) => {
  const [capacity, setCapacity] = useState<ExecutionCapacity | null>(null);
  const [loading,  setLoading]  = useState(true);

  const load = async () => {
    try {
      const data = await getExecutionCapacity();
      setCapacity(data);
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading || !capacity) return null;
  if (!capacity.available) return null;
  if (capacity.type !== 'enterprise' && capacity.type !== 'business') return null; // FREE/STANDARD : pas d'affichage de slots

  const pct      = capacity.total > 0 ? Math.round((capacity.used / capacity.total) * 100) : 0;
  const isEnt    = capacity.type === 'enterprise';
  const isBiz    = capacity.type === 'business';
  const isFull   = capacity.free === 0;
  const isWarn   = pct >= 75;

  const barColor = isFull  ? 'bg-red-500'
                 : isWarn  ? 'bg-orange-400'
                 : isBiz   ? 'bg-blue-500'
                 : isEnt   ? 'bg-purple-500'
                 : 'bg-gray-400';

  const textColor = isFull  ? 'text-red-600'
                  : isWarn  ? 'text-orange-600'
                  : isBiz   ? 'text-blue-600'
                  : isEnt   ? 'text-purple-600'
                  : 'text-gray-600';

  const badgeColor = isFull  ? 'bg-red-50 border-red-200'
                   : isWarn  ? 'bg-orange-50 border-orange-200'
                   : isBiz   ? 'bg-blue-50 border-blue-200'
                   : isEnt   ? 'bg-purple-50 border-purple-200'
                   : 'bg-gray-50 border-gray-200';

  const slotLabel = isBiz ? 'Slots BYOO' : 'Slots dédiés';

  if (compact) {
    // ── Version widget dashboard ─────────────────────────────────────────────
    return (
      <div className={`rounded-lg border p-3 ${badgeColor}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold ${textColor}`}>
            {slotLabel}
          </span>
          <span className={`text-xs font-bold ${textColor}`}>
            {capacity.used}/{capacity.total}
          </span>
        </div>

        {/* Barre */}
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>

        <div className="mt-1.5 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {capacity.free > 0
              ? `${capacity.free} libre${capacity.free > 1 ? 's' : ''}`
              : 'Complet'}
          </span>
          {(isEnt || isBiz) && capacity.agents_online !== undefined && (
            <span className="text-xs text-gray-400">
              {capacity.agents_online} agent{capacity.agents_online !== 1 ? 's' : ''} en ligne
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── Version complète page Executions ────────────────────────────────────────
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center gap-4 ${badgeColor}`}>
      {/* Label plan */}
      <div className="flex-shrink-0">
        <span className={`text-xs font-semibold uppercase tracking-wide ${textColor}`}>
          {isEnt ? `Plan ${capacity.plan}` : isBiz ? `BYOO · ${capacity.plan}` : `Partagé · ${capacity.plan}`}
        </span>
      </div>

      {/* Barre + chiffres */}
      <div className="flex-1 flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        <span className={`text-sm font-bold whitespace-nowrap ${textColor}`}>
          {capacity.used} / {capacity.total} slots
        </span>
      </div>

      {/* Statut */}
      <div className="flex-shrink-0 flex items-center gap-3">
        {(isEnt || isBiz) && capacity.agents_online !== undefined && (
          <span className="text-xs text-gray-500">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1 align-middle" />
            {capacity.agents_online} agent{capacity.agents_online !== 1 ? 's' : ''} en ligne
          </span>
        )}
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
          isFull
            ? 'bg-red-100 border-red-200 text-red-700'
            : capacity.free === 1
            ? 'bg-orange-100 border-orange-200 text-orange-700'
            : 'bg-green-100 border-green-200 text-green-700'
        }`}>
          {isFull
            ? 'Complet — file d\'attente active'
            : `${capacity.free} slot${capacity.free > 1 ? 's' : ''} libre${capacity.free > 1 ? 's' : ''}`}
        </span>
      </div>
    </div>
  );
};

export default CapacityBar;
